/**
 * What a line on the form sees above it: its parent and its root, as the form
 * holds them (Decision 6 of docs/plans/entry-lines-and-layout.md).
 *
 * A line names its parent and its root through two reference fields the
 * server makes and fills — bound by the purposes `parent` and `root`. A line
 * formula or a line default reads up the tree with a `hop` through one of
 * them, the hop node every formula already has. But while the entry is being
 * typed, two things are missing on the phone:
 *
 * 1. The line's values do not hold its parent and root ids. The server sets
 *    them on write, and the line schema the phone holds leaves both fields
 *    out (their bindings stay, so their ids are known).
 * 2. The rows they point at are not in the mirror: the entry is not saved.
 *
 * So this puts the two ids into the picture a recipe is worked out against,
 * and answers a hop to either from the form's own values, never the mirror.
 * The ids are taken out again before anything is saved (`withoutLineRefs`):
 * the server owns both fields, and a value sent for one is not the device's
 * to send.
 *
 * Pure, so `bun test` reaches it, and shared by `formula-fill.ts` and
 * `default-fill.ts`, which is the one reader up the tree on the device.
 */
import {
  formulaFieldsRead,
  formulaHopsRead,
  formulaOf,
  type EntryValues,
  type FormulaValues,
  type RecordSchemaResponse,
} from '@alluzo/shared';

/** One row above a line, as the form holds it. */
export type RowAbove = Readonly<{ entryId: string; values: EntryValues }>;

/**
 * Where a line sits. For a depth-1 line the parent is the entry, so `parent`
 * and `root` are the same row.
 */
export type LineAbove = Readonly<{ parent: RowAbove; root: RowAbove }>;

/** The ids of a line record's parent and root fields, from its bindings. */
export const lineRefFields = (
  lineSchema: RecordSchemaResponse,
): Readonly<{ parent: string | null; root: string | null }> => {
  const idOf = (answer: RecordSchemaResponse['bindings']['parent']): string | null =>
    answer !== undefined && 'fieldId' in answer ? answer.fieldId : null;
  return { parent: idOf(lineSchema.bindings.parent), root: idOf(lineSchema.bindings.root) };
};

/**
 * The picture a line's recipes read: its own values with its parent and root
 * ids in place, and the rows above it by id, for `known`.
 */
export const lineContext = (
  lineSchema: RecordSchemaResponse,
  values: EntryValues,
  above: LineAbove,
): Readonly<{ values: EntryValues; known: ReadonlyMap<string, FormulaValues> }> => {
  const refs = lineRefFields(lineSchema);
  const withRefs: Record<string, unknown> = { ...values };
  if (refs.parent !== null) withRefs[refs.parent] = above.parent.entryId;
  if (refs.root !== null) withRefs[refs.root] = above.root.entryId;
  const known = new Map<string, FormulaValues>([
    // The root first, so a depth-1 line, whose parent IS its root, reads one row.
    [above.root.entryId, above.root.values as FormulaValues],
    [above.parent.entryId, above.parent.values as FormulaValues],
  ]);
  return { values: withRefs as EntryValues, known };
};

/** The same values with the parent and root ids taken out again. */
export const withoutLineRefs = (lineSchema: RecordSchemaResponse, values: EntryValues): EntryValues => {
  const refs = lineRefFields(lineSchema);
  if (refs.parent === null && refs.root === null) return values;
  const out: Record<string, unknown> = {};
  for (const [fieldId, value] of Object.entries(values)) {
    if (fieldId === refs.parent || fieldId === refs.root) continue;
    out[fieldId] = value;
  }
  return out as EntryValues;
};

/**
 * The entry fields a line's formula, default or show-when rule reads, at any
 * depth. A line reads the entry through a hop up the row it hangs from (a
 * room's Nights reads the stay's Check out), so the form has to watch each of
 * these: without it, a change to Check out or to the stay's status left every
 * open line on the figure and the fields it was drawn with.
 */
export const entryFieldsReadByLines = (schema: RecordSchemaResponse): ReadonlySet<string> => {
  const entryIds = new Set(schema.fields.map((field) => field.id));
  const out = new Set<string>();
  const readFrom = (lineSchema: RecordSchemaResponse): void => {
    for (const field of lineSchema.fields) {
      const specs = [
        field.type === 'formula' ? formulaOf(field.spec) : null,
        field.startsWith ?? null,
        field.showWhen ?? null,
      ];
      for (const spec of specs) {
        if (spec === null) continue;
        for (const fieldId of formulaFieldsRead(spec)) if (entryIds.has(fieldId)) out.add(fieldId);
        for (const hop of formulaHopsRead(spec)) if (entryIds.has(hop.fieldId)) out.add(hop.fieldId);
      }
    }
    for (const deeper of Object.values(lineSchema.lineSchemas ?? {})) readFrom(deeper);
  };
  for (const lineSchema of Object.values(schema.lineSchemas ?? {})) readFrom(lineSchema);
  return out;
};
